<script setup lang="ts">
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { OptionType } from '@/enums/OptionType';
  import { IItemOption } from '@/interfaces/IItemOption';
  import { computed } from 'vue';

  /** Item-editor row: a linked option (persisted) or an unsaved draft. */
  export interface IItemOptionRow extends IItemOption {
    isDraft?: boolean;
  }

  const props = defineProps<{
    itemOption: IItemOptionRow;
    first: boolean;
    last: boolean;
  }>();

  defineEmits<{
    (e: 'moveUp'): void;
    (e: 'moveDown'): void;
    (e: 'edit'): void;
    (e: 'remove'): void;
  }>();

  const option = computed(() => props.itemOption.option);

  const isRepeatable = computed(() => option.value?.type === OptionType.Add);

  const choiceCount = computed(
    () => (option.value?.optionItems ?? []).filter((oI) => !oI.deletedAt).length
  );
</script>

<template>
  <div class="item-option-row">
    <div class="row-summary">
      <div class="row-heading">
        <span class="row-title">{{ option.title }}</span>
        <span v-if="isRepeatable" class="row-badge type">
          {{ $t('optionEditor.repeatableBadge') }}
        </span>
        <span v-if="itemOption.isDraft" class="row-badge draft">
          {{ $t('itemOptions.draftBadge') }}
        </span>
      </div>
      <span class="row-subtitle">
        {{
          $tc('itemOptions.choiceCount', choiceCount, { count: choiceCount })
        }}
      </span>
    </div>

    <div class="row-controls">
      <div class="row-visibility">
        <v-switch
          v-model="itemOption.active"
          class="visibility-switch"
          color="green"
          dense
          hide-details
        />
        <span class="visibility-label">
          {{ $t('itemOptions.visibleInItem') }}
        </span>
      </div>

      <div class="row-buttons">
        <button
          class="row-icon-button"
          :disabled="first"
          :title="$t('itemOptions.moveUp')"
          @click="$emit('moveUp')"
        >
          <PosUiIcon name="arrow-up" />
        </button>
        <button
          class="row-icon-button"
          :disabled="last"
          :title="$t('itemOptions.moveDown')"
          @click="$emit('moveDown')"
        >
          <PosUiIcon name="arrow-down" />
        </button>
        <button
          class="row-icon-button"
          :title="$t('itemOptions.edit')"
          @click="$emit('edit')"
        >
          <PosUiIcon name="pencil-outline" />
        </button>
        <button
          class="row-icon-button remove"
          :title="$t('itemOptions.removeFromItem')"
          @click="$emit('remove')"
        >
          <PosUiIcon name="close" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .item-option-row {
    @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2;

    .row-summary {
      @apply tw-flex tw-min-w-0 tw-flex-col;

      .row-heading {
        @apply tw-flex tw-flex-wrap tw-items-center tw-gap-1.5;

        .row-title {
          @apply tw-text-sm tw-font-medium tw-text-neutral-900;
        }

        .row-badge {
          @apply tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs;

          &.type {
            @apply tw-bg-neutral-100 tw-text-neutral-600;
          }

          &.draft {
            @apply tw-bg-amber-50 tw-text-amber-700;
          }
        }
      }

      .row-subtitle {
        @apply tw-text-xs tw-text-neutral-500;
      }
    }

    .row-controls {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;

      .row-visibility {
        @apply tw-flex tw-items-center tw-gap-1;

        .visibility-switch {
          @apply tw-m-0 tw-p-0;
        }

        .visibility-label {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }

      .row-buttons {
        @apply tw-flex tw-items-center;

        .row-icon-button {
          @apply tw-flex tw-h-11 tw-w-10 tw-items-center tw-justify-center tw-rounded-lg tw-text-lg tw-text-neutral-500 tw-transition-colors;

          &:hover {
            @apply tw-bg-neutral-100 tw-text-neutral-900;
          }

          &.remove:hover {
            @apply tw-bg-red-50 tw-text-red-600;
          }

          &:disabled {
            @apply tw-cursor-not-allowed tw-text-neutral-300;

            &:hover {
              @apply tw-bg-transparent tw-text-neutral-300;
            }
          }

          &:focus-visible {
            @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
          }
        }
      }
    }
  }
</style>
